// ── AR Flow — NOVA INTERFACE, Etapa 4: Prospecção em colunas (como Leads) ─────
// Só visual. Recebe a MESMA lista já filtrada da tela (data, busca, ordem,
// permissões) e, ao clicar, chama a mesma função que abre a conversa hoje
// (setChatOpenId, com a trava de follow-up). Não grava nada.

import TextSendStatus, { useTextSendPriority } from "../TextSendStatus";
import { persistedNextContactDate } from "../futureFollowUpGuard.mjs";

const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const brl = v => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

function shortDate(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, m, d] = value.split("-");
    return `${d}/${m}`;
  }
  const t = new Date(value);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export default function ProspKanban({ theme, stages, items:sourceItems, returnsDue = [], today = "", isAwaiting, unreadCount, consultantName, showConsultant, onOpen, onToggleList, listOpen, fila = null, onOpenFilaItem = null, onMarcarLiguei = null, filaReadOnly = false, openingFilaItemId = null }) {
  const items = useTextSendPriority(sourceItems);
  const th = theme;
  const line = th.nuLine || th.sandDark;
  const knownStages = new Set(stages.map(stage => stage.id));

  return (
    <section aria-label="Prospecção por etapa" data-save-diagnostic-list="visible"
      data-save-diagnostic-ready={fila?.carregando ? "false" : "true"} style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ fontSize: 14, color: th.muted }}>
          {items.length} contato{items.length === 1 ? "" : "s"} · clique no cartão para abrir a conversa
        </div>
        <button type="button" onClick={onToggleList} style={{
          border: `1px solid ${line}`, background: listOpen ? th.accent : "#fff", color: listOpen ? "#fff" : th.text,
          borderRadius: 4, padding: "6px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
        }}>
          {listOpen ? "Esconder lista detalhada" : "Lista detalhada (editar, excluir)"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch" }}>
        {fila && fila.participa !== false && (
          <FilaColuna theme={th} line={line} fila={fila} onOpen={onOpenFilaItem} onLiguei={onMarcarLiguei}
            openingId={openingFilaItemId}
            readOnly={filaReadOnly} />
        )}
        <div role="region" aria-label="Retornos de hoje" style={{ flex:"0 0 240px", minWidth:240, border:`1px solid ${line}`, background:th.sand || "#FAF8F3", maxHeight:"calc(100vh - 260px)", overflowY:"auto" }}>
          <div style={{ borderTop:"3px solid #B45309", background:"#fff", padding:"10px 12px", fontWeight:700, fontSize:13 }}>
            Retornos de hoje <span style={{ color:th.muted }}>({returnsDue.length})</span>
          </div>
          <div style={{ padding:8 }}>
            {!returnsDue.length && <div style={{ color:th.muted, fontSize:12, padding:12 }}>Nenhum retorno para hoje.</div>}
            {returnsDue.map(p => <button key={p.id ?? p._firebaseKey} type="button" onClick={() => onOpen(p)}
              style={{ width:"100%", textAlign:"left", padding:"9px 10px", marginBottom:6, border:`1px solid ${line}`, borderLeft:"3px solid #B45309", borderRadius:4, background:"#fff", color:th.text, cursor:"pointer", fontFamily:"inherit" }}>
              <span style={{ display:"block", fontWeight:700 }}>{p.name || p.nome || "Cliente"}</span>
              <span style={{ display:"block", fontSize:11, color:th.muted, marginTop:4 }}>
                {shortDate(persistedNextContactDate(p))} · {persistedNextContactDate(p) < today ? "Atrasado" : "Hoje"}
                {showConsultant ? ` · ${consultantName(p.consultantId)}` : ""}
              </span>
            </button>)}
          </div>
        </div>
        {stages.map(stage => {
          // A lista antiga mostra etapas ausentes/legadas na primeira coluna.
          // Não deixe esses contatos desaparecerem no quadro novo.
          const col = items.filter(p => p.stage === stage.id || (stage.id === stages[0].id && !knownStages.has(p.stage)));
          const total = col.reduce((s, p) => s + (Number(p.value) || 0), 0);
          return (
            <div key={stage.id} style={{ flex: "0 0 240px", minWidth: 240, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 260px)", minHeight: 320 }}>
              <div style={{ borderTop: `3px solid ${stage.color}`, background: "#fff", border: `1px solid ${line}`, borderBottom: "none",
                padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: th.text }}>{stage.label}</span>
                <span style={{ fontFamily: MONO, fontSize: 12, color: th.muted }}>{col.length}</span>
              </div>
              <div style={{ background: th.sand || "#FAF8F3", border: `1px solid ${line}`, padding: 8, overflowY: "auto", flex: 1 }}>
                {total > 0 && <div style={{ fontFamily: MONO, fontSize: 11, color: th.muted, margin: "0 2px 8px" }}>{brl(total)}</div>}
                {col.length === 0 && <div style={{ color: th.muted, fontSize: 12, textAlign: "center", padding: "18px 0" }}>Nenhum contato</div>}
                {col.map(p => {
                  const awaiting = Boolean(isAwaiting(p));
                  const unread = Number(unreadCount(p)) || 0;
                  const next = shortDate(p.nextContactDate || p.followUp);
                  return (
                    <button key={p.id} type="button" onClick={() => onOpen(p)} style={{
                      width: "100%", textAlign: "left", background: "#fff", border: `1px solid ${awaiting ? "#F3C5C0" : line}`,
                      borderLeft: `3px solid ${awaiting ? "#B42318" : stage.color}`, borderRadius: 4, padding: "9px 10px",
                      marginBottom: 6, cursor: "pointer", fontFamily: "inherit", color: th.text,
                    }}>
                      <TextSendStatus storeId={p.storeId} phone={p.phone} readOnly={filaReadOnly} />
                      <span style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center" }}>
                        <span style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name || "Sem nome"}</span>
                        {unread > 0 && (
                          <span aria-label={`${unread} não lida${unread > 1 ? "s" : ""}`} style={{ background: "#B42318", color: "#fff", borderRadius: 99, padding: "1px 7px", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{unread}</span>
                        )}
                      </span>
                      {(p.product || Number(p.value) > 0) && (
                        <span style={{ display: "block", fontSize: 12, color: th.muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {[p.product, Number(p.value) > 0 ? brl(p.value) : null].filter(Boolean).join(" · ")}
                        </span>
                      )}
                      <span style={{ display: "block", fontSize: 11, marginTop: 4, color: awaiting ? "#B42318" : th.muted, fontWeight: awaiting ? 700 : 500 }}>
                        {[awaiting ? "Aguardando você" : null, next ? `Próximo: ${next}` : null, showConsultant ? consultantName(p.consultantId) : null].filter(Boolean).join(" · ") || "\u00A0"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Coluna "Prospectar hoje" (cota de novos configurada por consultor) ────────
function FilaColuna({ theme, line, fila, onOpen, onLiguei, readOnly, openingId = null }) {
  const th = theme;
  const dados = fila.fila;
  const itens = dados?.itens || [];
  const feitos = itens.filter(i => i.status === "feito").length;
  const pendentes = itens.filter(i => i.status !== "feito" && i.fonte !== "prospeccao_vencida");
  const ordenados = pendentes;
  return (
    <div style={{ flex: "0 0 260px", minWidth: 260, display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 260px)", minHeight: 320 }}>
      <div style={{ borderTop: `3px solid ${th.accent}`, background: "#fff", border: `1px solid ${line}`, borderBottom: "none", padding: "10px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontWeight: 800, fontSize: 13, color: th.text }}>
            {dados?.naoIniciado ? "Fila ainda não iniciou" : "Prospectar hoje"}
          </span>
          {!dados?.naoIniciado && (
            <span style={{ fontFamily: MONO, fontSize: 12, color: feitos >= itens.length && itens.length ? "#1E6B4A" : th.muted }}>{feitos} de {itens.length}</span>
          )}
        </div>
        {dados?.dailyTarget != null && <div style={{fontSize:11,color:th.muted,marginTop:5}}>
          Cota: {dados.dailyTarget} novos/dia · distribuídos hoje: {dados.newAssignedToday || 0}. Retornos não ocupam esta cota.
        </div>}
        {!dados?.naoIniciado && (
          <div aria-hidden="true" style={{ height: 4, background: th.sandDark || "#F4EBDD", borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
            <div style={{ width: `${itens.length ? Math.round((feitos / itens.length) * 100) : 0}%`, height: "100%", background: th.accent }} />
          </div>
        )}
      </div>
      <div style={{ background: th.sand || "#FAF8F3", border: `1px solid ${line}`, padding: 8, overflowY: "auto", flex: 1 }}>
        {readOnly && dados && (
          <div role="status" style={{ color:th.muted, fontSize:12, padding:"6px 2px 10px" }}>
            Modo consulta. Para abrir os cartões e enviar mensagens, entre com o login do consultor.
          </div>
        )}
        {readOnly && !fila.fila && !fila.carregando && !fila.erro && (
          <div style={{ color: th.muted, fontSize: 12, textAlign: "center", padding: "18px 0" }}>
            Fila indisponível.
          </div>
        )}
        {fila.carregando && !dados && <div style={{ color: th.muted, fontSize: 12, textAlign: "center", padding: "18px 0" }}>Carregando a fila de hoje…</div>}
        {fila.erro && <div style={{ color: "#B42318", fontSize: 12, padding: "6px 2px" }}>{fila.erro}</div>}
        {fila.participa === false && <div style={{ color: th.muted, fontSize: 12, textAlign: "center", padding: "18px 0" }}>Este consultor não participa do tombamento.</div>}
        {dados?.naoIniciado && (
          <div role="status" style={{ color: th.muted, fontSize: 12, textAlign: "center", padding: "18px 8px" }}>
            O tombamento começa em {shortDate(dados.inicioTombamento) || "data não informada"}.
          </div>
        )}
        {dados && !dados.naoIniciado && fila.participa !== false && !itens.length && <div style={{ color: th.muted, fontSize: 12, textAlign: "center", padding: "18px 0" }}>Nenhum cliente na fila de hoje.</div>}
        {itens.length > 0 && !pendentes.length && <div role="status" style={{ color:th.muted, fontSize:12, textAlign:"center", padding:"18px 0" }}>Todos os contatos de hoje foram concluídos.</div>}
        {ordenados.map(it => {
          const feito = it.status === "feito";
          const idx = itens.indexOf(it);
          return (
            <div key={it.id} style={{ background: "#fff", border: `1px solid ${line}`, borderLeft: `3px solid ${feito ? "#1E6B4A" : it.sobraDe ? "#B45309" : th.accent}`,
              borderRadius: 4, marginBottom: 6, opacity: feito ? 0.6 : 1 }}>
              {readOnly ? (
                <div aria-label={`${it.nome}${feito ? ", feito" : ""}`} style={{
                  width: "100%", textAlign: "left", background: "none", border: "none", padding: "9px 10px 6px", fontFamily: "inherit", color: th.text }}>
                  {conteudoItem(it, feito, th)}
                </div>
              ) : (
                <button type="button" disabled={!onOpen || openingId != null} aria-busy={openingId === String(it.id)} onClick={() => onOpen && onOpen(it)} style={{
                width: "100%", textAlign: "left", background: "none", border: "none", padding: "9px 10px 6px", cursor: onOpen ? "pointer" : "default", fontFamily: "inherit", color: th.text }}>
                  {conteudoItem(it, feito, th)}
                  {openingId === String(it.id) && <span role="status" style={{ display:"block", fontSize:11, marginTop:4 }}>Abrindo conversa…</span>}
                </button>
              )}
              {!readOnly && !feito && onLiguei && (
                <button type="button" onClick={() => onLiguei(it._queueIndex ?? idx)} style={{ background: "none", border: "none", color: th.muted, fontSize: 11, textDecoration: "underline", cursor: "pointer", padding: "0 10px 8px", fontFamily: "inherit" }}>
                  Resolvi por ligação
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function conteudoItem(it, feito, theme) {
  return (
    <>
      <span style={{ display: "block", fontWeight: 700, fontSize: 14, textDecoration: feito ? "line-through" : "none" }}>{it.nome}</span>
      <span style={{ display: "block", fontSize: 12, color: theme.muted, marginTop: 3 }}>{it.motivo}</span>
      {it.sobraDe && !feito && <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#B45309", marginTop: 3 }}>Ficou de ontem</span>}
      {feito && <span style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#1E6B4A", marginTop: 3 }}>Feito{it.feitoComo === "ligacao" ? " (ligação)" : ""}</span>}
    </>
  );
}